import { HOURS } from "@/model/Settings";
import type { Team } from "@/model/session";
import type { AuthUser } from "@/model/user";
import { SimpleCache } from "@/utils/SimpleCache";
import { authAPIUrl } from "@/utils/env/Environment";

import type {
  ServerAuthUserWithTeams,
  ServerTeamDetails,
  ServerTeamMember,
  ServerUserTeam,
} from "../serverModel";
import { createApiClient, modernInterceptor } from "./api.config";
import { mapUser, mapUserTeams } from "./mapBackendData";
import { addAlmMapping } from "./userAlmMapping";

const api = modernInterceptor(createApiClient(authAPIUrl));
const emptyListApi = modernInterceptor(createApiClient(authAPIUrl), {
  defaultValue: [],
});

export type UserWithTeams = AuthUser & { teams: Team[] };
export type TeamMember = AuthUser & { isAdmin: boolean };

export async function searchUsers(query: string): Promise<UserWithTeams[]> {
  const res = await emptyListApi.get<ServerAuthUserWithTeams[]>("/v1/users/search", {
    params: { query },
  });
  return await Promise.all(
    res.data.map((user) => addAlmMapping(mapUser(user), mapUserTeams(user.teams))),
  );
}

const teamUsers = new SimpleCache<TeamMember[]>({ timeout: HOURS });

export async function usersInTeam(team: Team): Promise<TeamMember[]> {
  return await teamUsers.loadItem(team.id, () => getUsersInTeam(team));
}

async function getUsersInTeam(team: Team): Promise<TeamMember[]> {
  const res = await emptyListApi.get<ServerTeamMember[]>(`/v1/teams/${team.id}/members`);
  return await Promise.all(
    res.data.map(async (user) => ({
      ...(await addAlmMapping(mapUser(user))),
      isAdmin: user.is_admin,
    })),
  );
}

export async function getTeamDescription(team: Team): Promise<string> {
  const res = await api.get<ServerTeamDetails>(`/v1/teams/${team.id}`);
  return res.data.description ?? "";
}

export async function getUserTeams(userId: string): Promise<Team[]> {
  const res = await emptyListApi.get<ServerUserTeam[]>(`/v1/users/${userId}/teams`);
  return mapUserTeams(res.data);
}
